import {
	jsonClass,
	jsonProperty,
	jsonArrayProperty,
} from '@sumbricht/json-class-serializer'
import { ScreenEditorInput } from '../decorators/screen-editor-input.js'
import { BoVisitor } from '../../bos/bo-visitor.js'
import { Block, TextBlock } from '../index.js'
import { DataTableBlock } from './data-table-block.js'
import { ContentAreaBlock } from './content-area-block.js'
import { FormInputBlock } from '../form/form-input-block.js'
import { BoReference } from '../../bos/bo-reference.js'
import { Expression } from '../../data/text-or-code.js'
import { TextOrCode_assumeCode } from '../../data/text-or-code.js'
import { TextOrCode_assumeText } from '../../data/text-or-code.js'
import { TextOrExpression_assumeExpression } from '../../data/text-or-code.js'
import { TextOrExpression_assumeText } from '../../data/text-or-code.js'

export const DataTableColumnStickyOptions = {
	'': 'None',
	sticky: 'Stick to left',
	stickyEnd: 'Stick to right',
} as const
export type DataTableColumnStickyOption =
	keyof typeof DataTableColumnStickyOptions

const ColumnFilterTypes = {
	none: 'None',
	withClear: 'With "clear" option',
	withoutClear: 'Without "clear" option',
	custom: 'Custom (ContentArea "filter")',
}
type ColumnFilterType = keyof typeof ColumnFilterTypes
const ColumnFilterSourceTypes = {
	text: 'Free text',
	staticEntity: 'StaticEntity',
	array: 'Array of objects',
}
type ColumnFilterSourceType = keyof typeof ColumnFilterSourceTypes

@jsonClass('DataTableColumnBlock')
export class DataTableColumnBlock extends Block {
	@jsonProperty(TextOrExpression_assumeText)
	@ScreenEditorInput<DataTableColumnBlock>({
		inputType: 'code',
		order: 1,
		label: 'Column ID',
		codeLanguage: 'ts',
		editorSize: 'singleline',
		isBinding: false,
		tsReturnType: () => 'string',
		isMandatory: (blocks) =>
			blocks[0].getTable().dataSource.sourceType == 'entity',
	})
	columnId = new TextOrExpression_assumeText()

	@jsonProperty()
	@ScreenEditorInput<DataTableColumnBlock>({
		inputType: 'checkbox',
		order: 2,
		label: 'Is editable',
		// onInitializedOrChanged: (colBlocks, properties) => {
		// 	for(const colBlock of colBlocks) {
		// 		if(colBlock.isEditable) {
		// 			colBlock.cellSource = 'expression'
		// 		}
		// 	}
		// }
	})
	isEditable: boolean = false

	@jsonProperty(TextOrExpression_assumeExpression)
	@ScreenEditorInput<DataTableColumnBlock>({
		inputType: 'code',
		order: 3,
		label: 'Header text',
		codeLanguage: 'ts',
		editorSize: 'singleline',
		isBinding: false,
		tsReturnType: () => 'string | number',
		isMandatory: () => true,
		alternativeContentAreaName: 'header',
	})
	headerTextCode = new TextOrExpression_assumeExpression('Column header', 'T')

	@jsonProperty(TextOrExpression_assumeExpression)
	@ScreenEditorInput<DataTableColumnBlock>({
		inputType: 'code',
		order: 4,
		label: 'Cell text',
		codeLanguage: 'ts',
		editorSize: 'singleline',
		isBinding: false,
		tsReturnType: () => 'string | number',
		isMandatory: () => true,
		alternativeContentAreaName: 'cell',
	})
	cellTextCode = new TextOrExpression_assumeExpression('Cell content', 'T')

	@jsonProperty(String)
	@ScreenEditorInput<DataTableColumnBlock>({
		inputType: 'dropdown',
		order: 20,
		label: 'Stickiness',
		options: Object.entries(DataTableColumnStickyOptions),
	})
	sticky: DataTableColumnStickyOption = ''

	@jsonProperty(String)
	@ScreenEditorInput<DataTableColumnBlock>({
		inputType: 'dropdown',
		order: 20,
		label: 'Column filter type',
		options: Object.entries(ColumnFilterTypes),
	})
	columnFilterType: ColumnFilterType = 'none'

	@jsonProperty()
	@ScreenEditorInput<DataTableColumnBlock>({
		inputType: 'checkbox',
		order: 20,
		tab: 'Advanced',
		label: 'Is sortable',
	})
	isSortable: boolean = false

	@jsonProperty(String)
	@ScreenEditorInput<DataTableColumnBlock>({
		inputType: 'dropdown',
		order: 21,
		tab: 'Advanced',
		label: 'Filter options source',
		options: Object.entries(ColumnFilterSourceTypes),
	})
	columnFilterSourceType: ColumnFilterSourceType = 'text'

	@jsonProperty(BoReference)
	@ScreenEditorInput<DataTableColumnBlock>({
		inputType: 'boRef',
		order: 22,
		tab: 'Advanced',
		label: 'Use StaticEntity for filtering',
		allowedBoTypes: ['StaticEntity'],
		includeImportedModules: true,
		isVisible: (blocks) =>
			(blocks[0].columnFilterType == 'withClear' ||
				blocks[0].columnFilterType == 'withoutClear') &&
			blocks[0].columnFilterSourceType == 'staticEntity',
	})
	filterStaticEntityRef: BoReference = new BoReference()

	@jsonProperty(Expression)
	@ScreenEditorInput<DataTableColumnBlock>({
		inputType: 'code',
		order: 23,
		label: 'Filter options array',
		tab: 'Advanced',
		codeLanguage: 'ts',
		editorSize: 'singleline',
		isBinding: false,
		tsReturnType: () => 'any[]',
		isVisible: (blocks) =>
			(blocks[0].columnFilterType == 'withClear' ||
				blocks[0].columnFilterType == 'withoutClear') &&
			blocks[0].columnFilterSourceType == 'array',
	})
	filterOptionsArrayCode = new Expression()

	@jsonProperty(Expression)
	@ScreenEditorInput<DataTableColumnBlock>({
		inputType: 'code',
		order: 24,
		label: 'Filter option ID',
		tab: 'Advanced',
		codeLanguage: 'ts',
		editorSize: 'singleline',
		isBinding: false,
		tsReturnType: () => 'string | number',
		additionalLines: (blocks: DataTableColumnBlock[]) => [
			`const $option = (${blocks[0].filterOptionsArrayCode.content})[0]`.replace(
				/\n/g,
				' ',
			),
		],
		additionalVariables: () => ({
			$index: 'number',
		}),
		isVisible: (blocks) =>
			(blocks[0].columnFilterType == 'withClear' ||
				blocks[0].columnFilterType == 'withoutClear') &&
			blocks[0].columnFilterSourceType == 'array',
		tooltip: () => ({
			text: 'Use <code>$option</code> to access each item of the options list and <code>$index</code> to access the zero-based index of the current item',
			acceptHtml: true,
		}),
	})
	filterOptionIdCode = new Expression()

	@jsonProperty(Expression)
	@ScreenEditorInput<DataTableColumnBlock>({
		inputType: 'code',
		order: 25,
		label: 'Filter option label',
		tab: 'Advanced',
		codeLanguage: 'ts',
		editorSize: 'singleline',
		isBinding: false,
		tsReturnType: () => 'string | number',
		additionalLines: (blocks: DataTableColumnBlock[]) => [
			`const $option = (${blocks[0].filterOptionsArrayCode.content})[0]`.replace(
				/\n/g,
				' ',
			),
		],
		additionalVariables: () => ({
			$index: 'number',
		}),
		isVisible: (blocks) =>
			(blocks[0].columnFilterType == 'withClear' ||
				blocks[0].columnFilterType == 'withoutClear') &&
			blocks[0].columnFilterSourceType == 'array',
		tooltip: () => ({
			text: 'Use <code>$option</code> to access each item of the options list and <code>$index</code> to access the zero-based index of the current item',
			acceptHtml: true,
		}),
	})
	filterOptionLabelCode = new Expression()

	@jsonProperty(Expression)
	@ScreenEditorInput<DataTableColumnBlock>({
		inputType: 'code',
		order: 26,
		tab: 'Advanced',
		label: 'Sort/filter value',
		tooltip: () => ({
			text: 'Optional alternative value to use for sorting and filtering',
		}),
		codeLanguage: 'ts',
		editorSize: 'singleline',
		isBinding: false,
		isVisible: (blocks) => {
			const tableBlock = blocks[0].getTable()
			if (!tableBlock) return false
			if (tableBlock.dataSource.sourceType != 'array') return false
			return true
		},
		tsReturnType: () => 'string | number | Date',
	})
	sortFilterValueCode = new Expression()

	constructor(init: Partial<DataTableColumnBlock> = {}) {
		super()
		this.init(init)
	}

	static createNewForEditor(): Block {
		return new this()
	}

	getColumnIdOrDefault() {
		return this.columnId.getAsExpressionOrDefault(`'column_${this.id}'`)
	}

	getEditorTitle() {
		let headerCode = this.headerTextCode.content
		const textBlock = this.getHeaderTextBlock()
		if (textBlock) headerCode = textBlock.textCode.content

		const [_, text] = (headerCode ?? '').match(/^\s*['"]([^'"]+)['"]\s*$/) ?? []
		if (text) {
			return `Column "${text}"`
		} else if (this.columnId.content?.trim()) {
			return `Column id=${this.columnId.content ?? ''}`
		} else {
			const colIdx = this.parent?.children.indexOf(this)
			const colNr = colIdx ? String(colIdx + 1) : '?'
			return `Column ${colNr}`
		}

		// return `${super.getEditorTitle()} ${this.columnId ?? ''}`
	}

	canHaveParent(parent: Block): boolean {
		return parent instanceof DataTableBlock
	}

	getHeaderTextBlock() {
		const contentArea = this.getContentArea('header')
		const textBlock = contentArea?.children.find(
			(b) => b instanceof TextBlock,
		) as TextBlock
		return textBlock
	}

	protected visitThisWithBoVisitor(
		visitor: BoVisitor,
		pathPrefix: (string | number)[],
	): void {}

	getEditorCategory(): string {
		return 'Tables & Lists'
	}

	get allowedStylePrefixes() {
		return ['header', 'filterCell']
	}

	getEffectiveSortAndFilterCode() {
		if (this.columnFilterType == 'none' && !this.isSortable) return ''

		if (this.sortFilterValueCode.isNotEmpty())
			return this.sortFilterValueCode.getAsExpression()
		const cellArea = this.getContentArea('cell')
		if (cellArea) {
			let firstFormInputBlock: FormInputBlock | undefined
			cellArea.visit(
				(block) => {
					if (!firstFormInputBlock && block instanceof FormInputBlock) {
						firstFormInputBlock = block as FormInputBlock
					}
				},
				this,
				0,
				'first',
			)
			if (firstFormInputBlock) {
				return firstFormInputBlock.bindingCode?.content
			}

			let firstTextBlock: TextBlock | undefined
			cellArea.visit(
				(block) => {
					if (!firstTextBlock && block instanceof TextBlock) {
						firstTextBlock = block as TextBlock
					}
				},
				this,
				0,
				'first',
			)
			if (firstTextBlock) {
				return firstTextBlock.textCode.getAsExpression()
			}

			return ''
		} else {
			return this.cellTextCode.getAsExpression()
		}
	}

	getTable() {
		return this.getFirstParent(
			(b) => b instanceof DataTableBlock,
		) as DataTableBlock
	}

	public getOwnVariablesForChildContentArea(
		contentArea: ContentAreaBlock,
	): Record<string, string | null> {
		if (contentArea.name == 'filter') {
			const table = this.getTable()
			const variables = {
				$table: 'any',
				[table.dataSource.loopVariableDefinition]: null,
			}
			if (table.dataSource.indexVariableDefinition) {
				variables[table.dataSource.indexVariableDefinition] = null
			}
			return variables
		}
		return {}
	}
}
